// Utility function for handling paginated API responses
import { createApiClient } from "./apiClient";

/**
 * Fetches all paginated data from a Webex Contact Center API endpoint
 * @param {string} relativeUri - The API endpoint URI
 * @param {string} accessToken - Access token for authentication
 * @param {Function} setProgress - Progress callback function
 * @param {string} resourceName - Name of the resource being fetched (for logging)
 * @param {number} pageSize - Items per page (default 100, max usually 1000)
 * @returns {Promise<Array>} - Array containing all items from all pages
 */
export const fetchAllPages = async (relativeUri, accessToken, setProgress, resourceName, pageSize = 100) => {
  const apiClient = createApiClient(accessToken);
  const allItems = [];
  let currentPage = 0; // Webex API pages start from 0
  let hasMorePages = true;

  setProgress((prev) => [
    ...prev,
    { type: 'info', message: `Fetching all ${resourceName} (pagination enabled)...`, timestamp: new Date().toLocaleTimeString() }
  ]);

  while (hasMorePages) {
    try {
      // Add Webex Contact Center pagination parameters to the URI
      const separator = relativeUri.includes('?') ? '&' : '?';
      const paginatedUri = `${relativeUri}${separator}page=${currentPage}&pageSize=${pageSize}`;

      setProgress((prev) => [
        ...prev,
        { type: 'info', message: `Fetching ${resourceName} page ${currentPage + 1}...`, timestamp: new Date().toLocaleTimeString() }
      ]);

      const response = await apiClient.get(paginatedUri);
      const result = response.data;

      // Handle Webex Contact Center response structure
      let pageItems = [];
      if (result.data && Array.isArray(result.data)) {
        pageItems = result.data;
      } else if (Array.isArray(result)) {
        pageItems = result;
      } else {
        console.warn(`Unexpected response structure for ${resourceName}:`, result);
        break;
      }

      // Add items from this page to our collection
      allItems.push(...pageItems);

      setProgress((prev) => [
        ...prev,
        { type: 'info', message: `Page ${currentPage + 1}: Found ${pageItems.length} ${resourceName}. Total so far: ${allItems.length}`, timestamp: new Date().toLocaleTimeString() }
      ]);

      // Check if there are more pages using Webex Contact Center meta information
      if (result.meta) {
        const { page, totalPages, totalRecords } = result.meta;
        
        // Check if we've reached the last page
        if (page >= totalPages - 1) {
          hasMorePages = false;
        }
        
        // Also check if we have all records
        if (allItems.length >= totalRecords) {
          hasMorePages = false;
        }
        
        setProgress((prev) => [
          ...prev,
          { type: 'info', message: `Progress: Page ${page + 1} of ${totalPages}, ${allItems.length} of ${totalRecords} total records`, timestamp: new Date().toLocaleTimeString() }
        ]);
      } else {
        // Fallback: if no meta information, check if we got fewer items than requested
        if (pageItems.length < pageSize) {
          hasMorePages = false;
        }
      }

      currentPage++;

      // Safety break to prevent infinite loops
      if (currentPage > 100) {
        setProgress((prev) => [
          ...prev,
          { type: 'warning', message: `Safety break: Stopped after 100 pages for ${resourceName}`, timestamp: new Date().toLocaleTimeString() }
        ]);
        break;
      }

    } catch (error) {
      console.error(`Error fetching ${resourceName} page ${currentPage + 1}:`, error);
      
      let errorMessage = `Error fetching ${resourceName} page ${currentPage + 1}: ${error.message || String(error)}`;
      
      if (error.response && error.response.data && error.response.data.error && error.response.data.error.reason) {
        errorMessage += ` (Reason: ${error.response.data.error.reason})`;
      }

      setProgress((prev) => [
        ...prev,
        { type: 'error', message: errorMessage, timestamp: new Date().toLocaleTimeString() }
      ]);

      // Stop on error
      throw error;
    }
  }

  setProgress((prev) => [
    ...prev,
    { type: 'success', message: `Successfully fetched all ${allItems.length} ${resourceName} from ${currentPage} pages.`, timestamp: new Date().toLocaleTimeString() }
  ]);

  return allItems;
};

/**
 * Legacy function wrapper for APIs that don't need pagination yet
 * This maintains backward compatibility while allowing gradual migration
 */
export const fetchSinglePage = async (relativeUri, accessToken, setProgress, resourceName) => {
  const apiClient = createApiClient(accessToken);
  
  setProgress((prev) => [
    ...prev,
    { type: 'info', message: `Fetching ${resourceName}...`, timestamp: new Date().toLocaleTimeString() }
  ]);

  try {
    const response = await apiClient.get(relativeUri);
    const result = response.data;
    const items = result.data || result;

    if (Array.isArray(items)) {
      setProgress((prev) => [
        ...prev,
        { type: 'success', message: `Successfully fetched ${items.length} ${resourceName}.`, timestamp: new Date().toLocaleTimeString() }
      ]);
      return items;
    } else {
      setProgress((prev) => [
        ...prev,
        { type: 'warning', message: `Fetched ${resourceName}, but response data was not an array.`, timestamp: new Date().toLocaleTimeString() }
      ]);
      return [];
    }
  } catch (error) {
    console.error(`Error fetching ${resourceName}:`, error);

    let errorMessage = `Error fetching ${resourceName}: ${error.message || String(error)}`;

    if (error.response && error.response.data && error.response.data.error && error.response.data.error.reason) {
      errorMessage += ` (Reason: ${error.response.data.error.reason})`;
    }

    setProgress((prev) => [
      ...prev,
      { type: 'error', message: errorMessage, timestamp: new Date().toLocaleTimeString() }
    ]);
    throw error;
  }
};